import { PaymentStatus } from "../../types/enums";
import { useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";

/**
 * Sorts a generic array of objects based on the provided field and direction.
 *
 * @template T - The type of the objects in the array.
 * @param {T[]} data - The array of objects to be sorted.
 * @param {string} field - The field in the object by which to sort.
 * @param {"asc" | "desc"} [direction="asc"] - The direction of the sort: "asc" for ascending, "desc" for descending.
 * @returns {T[]} - A new array sorted by the specified field and direction.
 *
 * @example
 * const sorted = sortByField(data, "amount", "desc");
 */
export function sortByField<T>(
  data: T[],
  field: string,
  direction: "asc" | "desc" = "asc"
): T[] {
  return [...data].sort((a, b) => {
    const aVal = a[field];
    const bVal = b[field];

    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return direction === "asc" ? 1 : -1;
    if (bVal == null) return direction === "asc" ? -1 : 1;

    // Convert to lowercase if strings
    if (typeof aVal === "string" && typeof bVal === "string") {
      return direction === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // Convert to time if Date objects
    if (aVal instanceof Date && bVal instanceof Date) {
      return direction === "asc"
        ? aVal.getTime() - bVal.getTime()
        : bVal.getTime() - aVal.getTime();
    }

    // Handle number sorting
    if (typeof aVal === "number" && typeof bVal === "number") {
      return direction === "asc" ? aVal - bVal : bVal - aVal;
    }

    // Fallback to default comparison for other types
    return direction === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });
}

/**
 * Resolves the display status text and color for a payment transaction.
 *
 * Priority of evaluation:
 *  1. Refunded → Always "Refunded" (orange).
 *  2. Pending refund → Orange for referenced refunds; black for unreferenced (credit return, transactionTypeId 1) refunds.
 *  3. Cancelled / Aborted:
 *      - If status is "Cancelled" or "Success" → "Cancelled" (orange).
 *      - If status is "Failed" / "Failure" / "Aborted" → "Aborted" (red).
 *  4. Failure → Always "Failed" (red).
 *  5. Pending states (empty, "Pending", "Link Sent") → "Pending" (black).
 *  6. Success / CardSaved → Green.
 *  7. Fallback → Keeps resolved status with black color.
 *
 * @param params - The transaction status parameters.
 * @param params.isRefunded - Whether the transaction was refunded.
 * @param params.isCancelled - Whether the transaction was cancelled.
 * @param params.cancelOrRefund - Whether the transaction was cancelled or refunded.
 * @param params.status - The raw status string (e.g., "Success", "Failed").
 * @param params.refundInitiated - Whether a refund has been initiated.
 *
 * @returns An object containing:
 *  - `text`: The resolved `PaymentStatus` text.
 *  - `color`: The corresponding color for the UI.
 */
export const resolvePaymentTransactionStatus = ({
  isRefunded = false,
  isCancelled = false,
  cancelOrRefund = false,
  status,
  refundInitiated = false,
  transactionTypeId = undefined
}): { text: string; color: string } => {
  const normalizedStatus = status?.toLowerCase();
  // Default
  let statusText: PaymentStatus = Object.values(PaymentStatus).includes(
    status as PaymentStatus
  )
    ? (status as PaymentStatus)
    : PaymentStatus.Pending;

  // Refund flow
  // Story 180258: "Pending Refund" is orange for referenced refunds. Unreferenced
  // refunds (vaulted-card credit return, transactionTypeId 1) stay black.
  if (refundInitiated)
    return {
      text: PaymentStatus.PendingRefund,
      color: transactionTypeId === 1 ? "black" : "orange"
    };
  if (isRefunded) return { text: PaymentStatus.Refunded, color: "orange" };

  // Cancelled / Aborted
  if (isCancelled || cancelOrRefund) {
    if (["cancelled", "success"].includes(normalizedStatus)) {
      return { text: PaymentStatus.Cancelled, color: "orange" };
    }
    if (["failure", "failed", "aborted"].includes(normalizedStatus)) {
      return { text: PaymentStatus.Aborted, color: "orange" };
    }
  }

  // Failure
  if (["failure", "failed"].includes(normalizedStatus)) {
    return { text: PaymentStatus.Failed, color: "red" };
  }

  // Pending states
  if (!statusText || ["pending", "link sent"].includes(normalizedStatus)) {
    return { text: PaymentStatus.Pending, color: "black" };
  }

  // Success / CardSaved
  if ([PaymentStatus.Success, PaymentStatus.CardSaved].includes(statusText)) {
    return { text: statusText, color: "green" };
  }

  // ChargeBack
  if (
    PaymentStatus.Chargeback.includes(statusText) ||
    PaymentStatus.RefundFailed.includes(statusText)
  ) {
    return { text: statusText, color: "red" };
  }

  // AutoReversal
  if (PaymentStatus.AutoReversal.includes(statusText)) {
    return { text: statusText, color: "orange" };
  }

  // Default fallback
  return { text: statusText, color: "black" };
};

/**
 * Resolves the display color for a given payment status.
 *
 * @param status - The payment status (e.g., Success, Pending, Refunded).
 * @param transactionTypeId - The type of transaction (special handling if `transactionTypeId === 1`).
 *
 * @returns The color code (as a string) representing the status:
 * - `"orange"` → Refunded, Cancelled, Aborted
 * - `"black"` → Pending, Link Sent, or refunded with `transactionTypeId === 1`; Pending Refund is black only when `transactionTypeId === 1` (unreferenced/credit-return), otherwise orange
 * - `"red"` → Failed or Failure
 * - `"green"` → Success or CardSaved
 * - `"black"` → Fallback for unknown statuses
 */
export const resolvePaymentStatusColor = (
  status: string,
  transactionTypeId: number
): string => {
  if (transactionTypeId === 1 && status === PaymentStatus.Refunded)
    return "black";

  switch (status) {
    case PaymentStatus.Refunded:
    case PaymentStatus.Cancelled:
    case PaymentStatus.Aborted:
    case PaymentStatus.AutoReversal:
      return "orange";

    case PaymentStatus.PendingRefund:
      // Story 180258: orange for referenced refunds, black for unreferenced (credit return).
      return transactionTypeId === 1 ? "black" : "orange";

    case PaymentStatus.Pending:
    case PaymentStatus.LinkSent:
      return "black";

    case PaymentStatus.Failure:
    case PaymentStatus.Failed:
    case PaymentStatus.Chargeback:
    case PaymentStatus.RefundFailed:
      return "red";

    case PaymentStatus.Success:
    case PaymentStatus.CardSaved:
      return "green";

    default:
      return "black"; // fallback
  }
};

export const isSurchargeEnabled = (): boolean => {
  return localStorage.getItem("hasSurchargeEverEnabled") === "true";
};

export const isPlatformFeeEnabled = (): boolean => {
  return localStorage.getItem("platformFee_flag_enabled") === "true";
};

export const IsActionColumnVisibleForTransactionsPage = (): boolean => {
  const showActionColumn = useAppSelector(
    (state: RootState) => state.transactionSettings.showActionColumn
  );

  return showActionColumn;
};

export const formatNumber = (
  value: number | string,
  isPinnedRow: boolean = false
): string => {
  const num = Number(value);
  if (isNaN(num)) return String(value);

  const absFormatted = Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const prefix = isPinnedRow ? "$" : "";

  return num < 0 ? `(${prefix}${absFormatted})` : `${prefix}${absFormatted}`;
};

// utils/encoding.ts

/**
 * Encodes a string to Base64 format.
 * Uses the browser-native encoding approach without deprecated APIs e.g. btoa is deprecated.
 * 
 * @param value - The string to encode.
 * @returns The Base64-encoded string.
 */
export const toBase64 = (value: string): string => {
    const bytes = new TextEncoder().encode(value);
    const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');
    return window.btoa(binString);
};